import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/data/site";

export interface SeoProps {
  title: string;
  description?: string;
  path: string;
  keywords?: string;
  ogImage?: string;
  noindex?: boolean;
  article?: {
    published: string;
    modified?: string;
    author?: string;
    section?: string;
  };
}

/**
 * Seo — renders the per-page head tags: title, description, canonical,
 * Open Graph, Twitter card, and robots. Feeds AI-powered search engines
 * (Perplexity, Google SGE, Claude, ChatGPT Search) clean entity markup.
 */
export function Seo({ title, description, path, keywords, ogImage, noindex, article }: SeoProps) {
  const fullTitle = title === SITE.name ? title : `${title} — ${SITE.name}`;
  const desc = description ?? SITE.description;
  const url = `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;
  const image = ogImage ?? `${SITE.url}${SITE.defaultOgImage}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE.name} — ${SITE.tagline}`} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Article-specific */}
      {article && <meta property="article:published_time" content={article.published} />}
      {article?.modified && <meta property="article:modified_time" content={article.modified} />}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}
    </Helmet>
  );
}
